import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { roleLabel } from '@/lib/roles';
import type { AmbienteAdmin, PapelAdmin, UsuarioAdmin } from '@/lib/plataforma_admin';
import {
  Blueprint,
  Button,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

interface UsuariosTableProps {
  usuarios: UsuarioAdmin[] | null;
  erro: string | null;
  salvando: boolean;
  onTrocarPapel: (usuarioId: string, papel: PapelAdmin) => Promise<void>;
  onDefinirAcesso: (usuarioId: string, ativo: boolean) => Promise<void>;
}

/**
 * Papéis oferecidos por ambiente — o mesmo recorte do servidor. // ux-neutrality:allow doc-comment
 *
 * Oferecer só o que cabe evita a recusa boba; mas quem decide continua sendo o // ux-neutrality:allow doc-comment
 * servidor, e uma tentativa fora do ambiente volta com a frase dele. // ux-neutrality:allow doc-comment
 */
export const PAPEIS_POR_AMBIENTE: Record<AmbienteAdmin, readonly PapelAdmin[]> = {
  banco: ['bank_manager', 'bank_user'],
  plataforma: ['owner', 'member', 'revisor', 'certificador'],
  escritorio: ['owner', 'member', 'revisor'],
};

function _rotuloAmbiente(ambiente: AmbienteAdmin): string {
  if (ambiente === 'plataforma') return ptBR.admin.ambientePlataforma;
  if (ambiente === 'banco') return ptBR.admin.ambienteBanco;
  return ptBR.admin.ambienteEscritorio;
}

/** Lista cross-tenant de pessoas com troca de papel e acesso (E53.F6). */
export function UsuariosTable({
  usuarios,
  erro,
  salvando,
  onTrocarPapel,
  onDefinirAcesso,
}: UsuariosTableProps) {
  const [papelEscolhido, setPapelEscolhido] = useState<Record<string, PapelAdmin>>({});
  // A confirmação vive na própria linha: a ação some do alcance de um clique
  // distraído sem tirar do usuário a possibilidade de prosseguir.
  const [confirmando, setConfirmando] = useState<string | null>(null);

  if (erro !== null) {
    return (
      <p data-testid="usuarios-erro" className="text-body-md text-danger-700">
        {erro}
      </p>
    );
  }
  if (usuarios === null) {
    return (
      <p data-testid="usuarios-carregando" className="text-body-md text-fg-muted">
        {ptBR.admin.usuariosCarregando}
      </p>
    );
  }
  if (usuarios.length === 0) {
    return (
      <p data-testid="usuarios-vazio" className="text-body-md text-fg-muted">
        {ptBR.admin.usuariosVazio}
      </p>
    );
  }

  return (
    <Blueprint className="min-w-0">
      <div className="min-w-0 overflow-x-auto [contain:layout]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{ptBR.admin.usuarioColPessoa}</TableHead>
              <TableHead>{ptBR.admin.usuarioColAmbiente}</TableHead>
              <TableHead>{ptBR.admin.usuarioColPapel}</TableHead>
              <TableHead>{ptBR.admin.usuarioColEstado}</TableHead>
              <TableHead>{ptBR.admin.usuarioColAcoes}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.map((usuario) => {
              const papelAtual = papelEscolhido[usuario.id] ?? usuario.role;
              const vinculo = usuario.banco_nome ?? usuario.escritorio_nome;
              return (
                <TableRow key={usuario.id} className="align-top" data-testid="usuario-linha">
                  <TableCell>
                    <span className="block text-fg">{usuario.display_name ?? usuario.email}</span>
                    {usuario.display_name !== null ? (
                      <span className="block font-mono text-caption text-fg-muted">
                        {usuario.email}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-fg-muted">
                    <span className="block">{_rotuloAmbiente(usuario.ambiente)}</span>
                    <span className="block text-caption">{vinculo ?? ptBR.admin.valorAusente}</span>
                  </TableCell>
                  <TableCell className="text-fg-muted" data-testid="usuario-papel">
                    {roleLabel(usuario.role)}
                  </TableCell>
                  <TableCell data-testid="usuario-estado">
                    <span className={usuario.ativo ? 'text-accent-700' : 'text-fg-muted'}>
                      {usuario.ativo ? ptBR.admin.estadoAtivo : ptBR.admin.estadoInativo}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="sr-only" htmlFor={`papel-${usuario.id}`}>
                        {ptBR.admin.trocarPapelLabel}
                      </label>
                      <Select
                        id={`papel-${usuario.id}`}
                        value={papelAtual}
                        disabled={salvando}
                        onChange={(evento) =>
                          setPapelEscolhido((atual) => ({
                            ...atual,
                            [usuario.id]: evento.target.value as PapelAdmin,
                          }))
                        }
                        data-testid={`papel-select-${usuario.id}`}
                      >
                        {PAPEIS_POR_AMBIENTE[usuario.ambiente].map((papel) => (
                          <option key={papel} value={papel}>
                            {roleLabel(papel)}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="secondary"
                        size="sm"
                        type="button"
                        disabled={salvando}
                        onClick={() => void onTrocarPapel(usuario.id, papelAtual)}
                        data-testid={`papel-salvar-${usuario.id}`}
                      >
                        {ptBR.admin.trocarPapelSubmit}
                      </Button>

                      {confirmando === usuario.id ? (
                        <span
                          className="flex items-center gap-1.5"
                          data-testid={`confirmar-${usuario.id}`}
                        >
                          <span className="text-body-sm text-fg-muted">
                            {ptBR.admin.confirmarPergunta}
                          </span>
                          <Button
                            variant="secondary"
                            size="sm"
                            type="button"
                            disabled={salvando}
                            onClick={() => {
                              setConfirmando(null);
                              void onDefinirAcesso(usuario.id, !usuario.ativo);
                            }}
                            data-testid={`acesso-confirmar-${usuario.id}`}
                          >
                            {ptBR.admin.confirmarSim}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            type="button"
                            onClick={() => setConfirmando(null)}
                            data-testid={`acesso-cancelar-${usuario.id}`}
                          >
                            {ptBR.admin.confirmarNao}
                          </Button>
                        </span>
                      ) : (
                        <Button
                          variant="secondary"
                          size="sm"
                          type="button"
                          disabled={salvando}
                          onClick={() => setConfirmando(usuario.id)}
                          data-testid={`acesso-${usuario.id}`}
                        >
                          {usuario.ativo ? ptBR.admin.desativarAcesso : ptBR.admin.reativarAcesso}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Blueprint>
  );
}
